/**
 * Security (/audit/advanced/security): A-F security grade + security findings summary of the audit (?job=), the
 * remediation approval settings (who can approve; MFA step-up toggle is owner-only) and the immutable audit trail of
 * remediation events. Flag audit.security_deep. Grade `not_measured` is shown as such, never as a pass (rule 2).
 */
import { useId, useState } from 'react';
import {
  APPROVER_ROLES, type AdvancedFinding, type RemediationEvent, type SecuritySettings,
} from '@qm/contracts/audit-remediation';
import { SEVERITIES } from '@qm/contracts/audit-analysis';
import { Badge, Button, DataTable, EmptyState, ErrorState, Heading, Loading, Toggle } from '@qm/ui';
import { useApp } from '../../../../../lib/app-context';
import { useFormat } from '../../../../../lib/format';
import { actionErrorKey, type SecuritySummary } from '../actions/client';
import { AdvancedPage, AUDIT_FLAGS, FailureState, useJobParam, useLoad, useRemediationClient, useRoles, type Load } from '../actions/context';
import { lt } from '../actions/ResolvePanel';
import '../actions/actions.css';

export const STEP_UP_AGES = [300, 600, 1800, 3600] as const;
export const securityFindings = (findings: readonly AdvancedFinding[]) => findings.filter((f) => f.source === 'security' || f.checkIds.some((c) => c.startsWith('security.')));
export const severityCounts = (findings: readonly AdvancedFinding[]) =>
  Object.fromEntries(SEVERITIES.map((s) => [s, findings.filter((f) => f.severity === s && !['verified', 'dismissed'].includes(f.status)).length])) as Record<(typeof SEVERITIES)[number], number>;

export function SecurityPage() {
  return <AdvancedPage area="security" flag={AUDIT_FLAGS.securityDeep}><SecurityScreen /></AdvancedPage>;
}

function SecurityScreen() {
  const job = useJobParam();
  const settings = useLoad('security:settings', (c) => c.securitySettings());
  const events = useLoad('security:events', (c) => c.events());
  const grade = useLoad(job ? `security:${job}` : null, async (c) => {
    const [summary, report] = await Promise.all([c.securitySummary(job!), c.report(job!)]);
    return { summary, findings: securityFindings(report.findings) };
  });
  return <SecurityView job={job} grade={grade} settings={settings} events={events} onSettings={(s) => settings.set(s)} />;
}

export function SecurityView({ job, grade, settings, events, onSettings }: {
  job: string | null; grade: Load<{ summary: SecuritySummary; findings: AdvancedFinding[] }>; settings: Load<SecuritySettings>; events: Load<RemediationEvent[]>; onSettings: (s: SecuritySettings) => void;
}) {
  const { t } = useApp();
  return (
    <div className="ar-stack">
      <section className="ar-section" aria-labelledby="sec-grade">
        <Heading level={2} id="sec-grade">{t('audit-remediation.security.grade.title')}</Heading>
        {!job ? <p className="ar-muted">{t('audit-remediation.security.grade.needAudit')}</p>
          : grade.status === 'loading' ? <Loading label={t('common.loading')} />
            : grade.status === 'failed' ? <FailureState failure={grade.failure} notFoundKey="audit-remediation.security.grade.notFound" />
              : <GradeView summary={grade.data.summary} findings={grade.data.findings} />}
      </section>
      <section className="ar-section" aria-labelledby="sec-settings">
        <Heading level={2} id="sec-settings">{t('audit-remediation.security.settings.title')}</Heading>
        {settings.status === 'loading' ? <Loading label={t('common.loading')} />
          : settings.status === 'failed' ? <FailureState failure={settings.failure} />
            : <SettingsForm key={settings.data.updatedAt ?? 'default'} settings={settings.data} onSaved={onSettings} />}
      </section>
      <section className="ar-section" aria-labelledby="sec-trail">
        <Heading level={2} id="sec-trail">{t('audit-remediation.security.trail.title')}</Heading>
        <TrailView events={events} />
      </section>
    </div>
  );
}

export function GradeView({ summary, findings }: { summary: SecuritySummary; findings: AdvancedFinding[] }) {
  const { t, locale } = useApp();
  const f = useFormat();
  const counts = severityCounts(findings);
  const measured = summary.grade !== 'not_measured';
  return (
    <div className="ar-grade" data-testid="security-grade" data-grade={summary.grade}>
      <div className="ar-grade__mark" aria-hidden="true">{measured ? summary.grade : '—'}</div>
      <div className="ar-grade__text">
        <p className="ar-grade__label">{measured ? t('audit-remediation.security.grade.label', { grade: summary.grade, score: summary.score ?? '—' }) : t('audit-remediation.security.grade.notMeasured')}</p>
        <p className="ar-muted">{t('audit-remediation.security.grade.checks', { n: summary.checksRun })}</p>
        <ul className="ar-counts">
          {SEVERITIES.map((s) => <li key={s}><Badge tone={s === 'critical' || s === 'high' ? 'error' : s === 'medium' ? 'warning' : 'neutral'}>{t(`audit-remediation.severity.${s}`)}</Badge> {f.number(counts[s])}</li>)}
        </ul>
      </div>
      {findings.length ? (
        <ul className="ar-findings">
          {[...findings].sort((a, b) => b.priority - a.priority).slice(0, 10).map((x) => (
            <li key={x.id}><bdi>{f.text(lt(x.title, locale))}</bdi> <Badge>{t(`audit-remediation.status.${x.status}`)}</Badge></li>
          ))}
        </ul>
      ) : <p>{t('audit-remediation.security.grade.noFindings')}</p>}
      {summary.notMeasured.length ? (
        <details className="ar-notmeasured">
          <summary>{t('audit-remediation.security.grade.notMeasuredList', { n: summary.notMeasured.length })}</summary>
          <ul>{summary.notMeasured.map((n) => <li key={n.checkId}><code dir="ltr">{n.checkId}</code> · <bdi>{f.text(n.reason)}</bdi></li>)}</ul>
        </details>
      ) : null}
    </div>
  );
}

export function SettingsForm({ settings, onSaved }: { settings: SecuritySettings; onSaved: (s: SecuritySettings) => void }) {
  const { t } = useApp();
  const f = useFormat();
  const roles = useRoles();
  const client = useRemediationClient();
  const id = useId();
  const [approvers, setApprovers] = useState<SecuritySettings['approverRoles']>(settings.approverRoles);
  const [stepUp, setStepUp] = useState(settings.stepUpRequired);
  const [maxAge, setMaxAge] = useState(settings.stepUpMaxAgeSec);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const canEdit = roles.manage;
  const toggleRole = (r: (typeof APPROVER_ROLES)[number]) =>
    setApprovers((cur) => (cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r]));
  const save = async () => {
    if (!approvers.length) return setError('audit-remediation.security.settings.oneRole');
    setBusy(true); setError(null); setSaved(false);
    try {
      onSaved(await client.saveSecuritySettings({
        approverRoles: approvers, stepUpMaxAgeSec: maxAge,
        // step-up is owner-only: an admin never sends the field
        ...(roles.owner ? { stepUpRequired: stepUp } : {}),
      }));
      setSaved(true);
    } catch (e) { setError(actionErrorKey(e)); } finally { setBusy(false); }
  };
  return (
    <div className="ar-settings" data-testid="security-settings">
      <fieldset className="ar-radios" disabled={!canEdit}>
        <legend>{t('audit-remediation.security.settings.approvers')}</legend>
        {APPROVER_ROLES.map((r) => (
          <label key={r} className="ar-check"><input type="checkbox" checked={approvers.includes(r)} onChange={() => toggleRole(r)} /><span>{t(`audit-remediation.security.role.${r}`)}</span></label>
        ))}
      </fieldset>
      <div className="ar-toggle-row">
        <span id={`${id}-su`}>{t('audit-remediation.security.settings.stepUp')}</span>
        <Toggle checked={stepUp} onChange={setStepUp} disabled={!roles.owner} label={t('audit-remediation.security.settings.stepUp')} />
      </div>
      <p className="ar-muted">{t(roles.owner ? 'audit-remediation.security.settings.stepUpHint' : 'audit-remediation.security.settings.ownerOnly')}</p>
      <div className="cu-field">
        <label htmlFor={`${id}-age`}>{t('audit-remediation.security.settings.maxAge')}</label>
        <select id={`${id}-age`} value={maxAge} disabled={!canEdit || !stepUp} onChange={(e) => setMaxAge(Number(e.target.value))}>
          {STEP_UP_AGES.map((s) => <option key={s} value={s}>{t('audit-remediation.security.settings.minutes', { n: s / 60 })}</option>)}
        </select>
      </div>
      {settings.updatedAt ? <p className="ar-muted">{t('audit-remediation.security.settings.updated', { when: f.dateTime(settings.updatedAt) })}</p> : null}
      {canEdit ? <div className="cu-btnrow"><Button variant="accent" disabled={busy} onClick={() => void save()} data-testid="save-settings">{t('audit-remediation.security.settings.save')}</Button></div>
        : <p className="ar-muted">{t('audit-remediation.security.settings.readOnly')}</p>}
      <p role="status" aria-live="polite" className="ar-muted">{saved ? t('audit-remediation.security.settings.saved') : ''}</p>
      {error ? <ErrorState message={t(error)} /> : null}
    </div>
  );
}

export function TrailView({ events }: { events: Load<RemediationEvent[]> }) {
  const { t } = useApp();
  const f = useFormat();
  if (events.status === 'loading') return <Loading label={t('common.loading')} />;
  if (events.status === 'failed') return <FailureState failure={events.failure} />;
  if (!events.data.length) return <EmptyState title={t('audit-remediation.security.trail.empty')} />;
  return (
    <DataTable caption={t('audit-remediation.security.trail.title')} rows={events.data} columns={[
      { key: 'at', header: t('audit-remediation.security.trail.when'), render: (e) => f.dateTime(e.at) },
      { key: 'kind', header: t('audit-remediation.security.trail.event'), render: (e) => t(`audit-remediation.security.event.${e.kind}`) },
      { key: 'findingId', header: t('audit-remediation.security.trail.finding'), render: (e) => <code dir="ltr">{e.findingId}</code> },
      { key: 'actor', header: t('audit-remediation.security.trail.actor'), render: (e) => t(`audit-remediation.security.actor.${e.actorType}`) },
    ]} />
  );
}

